import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Modal } from '../components/Modal';
import { ProposalCard } from '../components/ProposalCard';
import { useEvent } from '../hooks/useEvent';
import { createProposal, updateProposal, deleteProposal, restoreProposal, fetchParticipants } from '../lib/api';
import type { Participant } from '../lib/api';
import { PersonPicker } from '../components/PersonPicker';
import { TemplateFields } from '../components/TemplateFields';
import {
  SessionBoardFilters,
  FilterToggleButton,
  NoResultsMessage,
} from '../components/SessionBoardFilters';
import { setNickname, getNickname } from '../lib/db';
import { getVotedProposalIds } from '../lib/interestUtils';
import type { ProposalFormData, Proposal, Template } from '../types';

/**
 * Compute the set of template field names that should be hidden because
 * there is only 0 or 1 option available (no meaningful choice).
 * Also returns a map of auto-fill values for those single-option fields.
 */
function computeSingleOptionFields(template: Template | null) {
  const hidden = new Set<string>();
  const autoFill: Record<string, string> = {};

  if (!template) return { hidden, autoFill };

  if ((template.languageOptions?.length ?? 0) <= 1) {
    hidden.add('language');
    if (template.languageOptions?.length === 1) autoFill['language'] = template.languageOptions[0];
  }
  if ((template.formatOptions?.length ?? 0) <= 1) {
    hidden.add('format');
    if (template.formatOptions?.length === 1) autoFill['format'] = template.formatOptions[0];
  }
  if ((template.levelOptions?.length ?? 0) <= 1) {
    hidden.add('level');
    if (template.levelOptions?.length === 1) autoFill['level'] = template.levelOptions[0];
  }
  if ((template.tagOptions?.length ?? 0) <= 1) {
    hidden.add('tags');
    if (template.tagOptions?.length === 1) autoFill['tags'] = template.tagOptions[0];
  }

  // Also check generic select fields with <=1 options
  for (const field of template.fields) {
    if (hidden.has(field.name)) continue; // already handled above
    if (field.type === 'select') {
      const opts = field.options ?? [];
      if (opts.length <= 1) {
        hidden.add(field.name);
        if (opts.length === 1) autoFill[field.name] = opts[0];
      }
    }
  }

  return { hidden, autoFill };
}

export function ProposalsPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    event,
    proposals,
    sessions,
    template,
    timeslots,
    myVotes,
    isLoading,
    isOrganizer,
    hasAccessCode,
    deviceId,
    nickname: currentNickname,
    refresh,
  } = useEvent(eventId ?? null);

  const defaultSlotMinutes = event?.defaultSlotDurationMinutes ?? 15;

  const slotSpanOptions = useMemo(() => {
    const blocks = new Map<string, number>();
    timeslots.filter((t) => !t.isBreak).forEach((t) => {
      const k = t.blockId ?? t.id;
      blocks.set(k, (blocks.get(k) ?? 0) + 1);
    });
    const max = Math.max(0, ...blocks.values(), 1);
    return Array.from({ length: Math.min(max, 12) }, (_, i) => i + 1);
  }, [timeslots]);

  // Set of proposal IDs the current user has voted / shown interest for
  const votedProposalIds = useMemo(() => getVotedProposalIds(myVotes), [myVotes]);

  const isLoggedIn = hasAccessCode || isOrganizer;

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState<Proposal | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterLanguage, setFilterLanguage] = useState('');
  const [filterFormat, setFilterFormat] = useState('');
  const [filterMine, setFilterMine] = useState(false);
  const [filterInterested, setFilterInterested] = useState(false);
  const [filterTag, setFilterTag] = useState('');
  const [editingProposal, setEditingProposal] = useState<Proposal | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);

  // Load known participants for this event (for person picker)
  useEffect(() => {
    if (eventId) {
      fetchParticipants(eventId).then(setParticipants).catch(() => {});
    }
  }, [eventId]);

  // Auto-open create modal when navigated with ?create=true
  useEffect(() => {
    if (searchParams.get('create') === 'true' && canPropose && !isLoading) {
      setShowCreateModal(true);
      setSearchParams({}, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  // Build a map from proposal ID -> session info for session grouping display
  const proposalSessionMap = useMemo(() => {
    const map = new Map<string, { sessionTitle: string; otherProposalTitles: string[] }>();
    for (const session of sessions) {
      if (session.proposals.length > 0) {
        for (const sp of session.proposals) {
          map.set(sp.id, {
            sessionTitle: session.title,
            otherProposalTitles: session.proposals.filter((p) => p.id !== sp.id).map((p) => p.title),
          });
        }
      }
    }
    return map;
  }, [sessions]);

  // Filter proposals
  const filteredProposals = useMemo(() => {
    return proposals.filter((p) => {
      // Search
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch =
          p.title.toLowerCase().includes(query) ||
          p.description?.toLowerCase().includes(query) ||
          p.createdByNickname.toLowerCase().includes(query) ||
          (p.people ?? []).some((person) => person.nickname.toLowerCase().includes(query)) ||
          Object.values(p.fields || {}).some((v) =>
            String(v).toLowerCase().includes(query)
          );
        if (!matchesSearch) return false;
      }

      // Language filter
      if (filterLanguage && p.fields?.language !== filterLanguage) {
        return false;
      }

      // Format filter
      if (filterFormat && p.fields?.format !== filterFormat) {
        return false;
      }

      // My proposals filter: show only proposals created by current user's device
      if (filterMine && deviceId) {
        if (p.createdByDeviceId !== deviceId) {
          return false;
        }
      }

      // Tag filter (tags stored as comma-separated or array in fields.tags)
      if (filterTag) {
        const proposalTags = p.fields?.tags;
        if (!proposalTags) return false;
        const tagsList = Array.isArray(proposalTags)
          ? proposalTags
          : String(proposalTags).split(',').map((s: string) => s.trim()).filter(Boolean);
        if (!tagsList.includes(filterTag)) return false;
      }

      // Interest filter: cards the user showed interest in
      if (filterInterested && !votedProposalIds.has(p.id)) {
        return false;
      }

      return true;
    });
  }, [proposals, searchQuery, filterLanguage, filterFormat, filterMine, filterTag, filterInterested, deviceId, votedProposalIds]);

  // Sort by display ID (most recent first)
  const sortedProposals = useMemo(() => {
    return [...filteredProposals].sort((a, b) => (b.displayId ?? 0) - (a.displayId ?? 0));
  }, [filteredProposals]);

  const canPropose = hasAccessCode || isOrganizer;

  const hasActiveFilters = !!(
    searchQuery ||
    filterLanguage ||
    filterFormat ||
    filterTag ||
    filterMine ||
    filterInterested
  );

  const clearFilters = () => {
    setSearchQuery('');
    setFilterLanguage('');
    setFilterFormat('');
    setFilterTag('');
    setFilterMine(false);
    setFilterInterested(false);
  };

  // Determine which fields to hide (single option = no meaningful choice)
  const { hidden: hiddenFields, autoFill: autoFillFields } = useMemo(
    () => computeSingleOptionFields(template),
    [template]
  );

  const handleCreateProposal = async (data: ProposalFormData) => {
    if (!eventId) return;

    await createProposal(eventId, data);
    await setNickname(data.nickname);
    refresh();
  };

  // Get unique values for filters – prefer template options, fall back to proposal-derived
  const languages = useMemo(() => {
    if (template?.languageOptions && template.languageOptions.length > 0) return template.languageOptions;
    const set = new Set<string>();
    proposals.forEach((p) => { if (p.fields?.language) set.add(p.fields.language); });
    return Array.from(set);
  }, [proposals, template]);

  const formats = useMemo(() => {
    if (template?.formatOptions && template.formatOptions.length > 0) return template.formatOptions;
    const set = new Set<string>();
    proposals.forEach((p) => { if (p.fields?.format) set.add(p.fields.format); });
    return Array.from(set);
  }, [proposals, template]);

  const tags = useMemo(() => {
    if (template?.tagOptions && template.tagOptions.length > 0) return template.tagOptions;
    const set = new Set<string>();
    proposals.forEach((p) => {
      if (p.fields?.tags) {
        String(p.fields.tags).split(',').map((s) => s.trim()).filter(Boolean).forEach((t) => set.add(t));
      }
    });
    return Array.from(set);
  }, [proposals, template]);

  if (isLoading) {
    return (
      <Layout title={event?.name} eventId={eventId} isOrganizer={isOrganizer} nickname={currentNickname} deviceId={deviceId}>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="inline-block w-8 h-8 border-4 border-primary-200 border-t-primary-400 rounded-full animate-spin" />
            <p className="mt-2 text-gray-500">Loading proposals...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={event?.name} eventId={eventId} isOrganizer={isOrganizer} nickname={currentNickname} deviceId={deviceId}>
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Session Cards</h1>
          {canPropose && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn btn-primary"
            >
              Submit Session Card
            </button>
          )}
        </div>

        {/* Filters */}
        <SessionBoardFilters>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <label htmlFor="search" className="sr-only">
                Search session cards
              </label>
              <input
                id="search"
                type="search"
                className="input"
                placeholder="Search session cards..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <div className="flex flex-wrap gap-2">
              {languages.length > 1 && (
                <select
                  className="select text-sm"
                  value={filterLanguage}
                  onChange={(e) => setFilterLanguage(e.target.value)}
                  aria-label="Filter by language"
                >
                  <option value="">All Languages</option>
                  {languages.map((lang) => (
                    <option key={lang} value={lang}>
                      {lang}
                    </option>
                  ))}
                </select>
              )}

              {formats.length > 1 && (
                <select
                  className="select text-sm"
                  value={filterFormat}
                  onChange={(e) => setFilterFormat(e.target.value)}
                  aria-label="Filter by format"
                >
                  <option value="">All Formats</option>
                  {formats.map((format) => (
                    <option key={format} value={format}>
                      {format}
                    </option>
                  ))}
                </select>
              )}

              {tags.length > 1 && (
                <select
                  className="select text-sm"
                  value={filterTag}
                  onChange={(e) => setFilterTag(e.target.value)}
                  aria-label="Filter by tag"
                >
                  <option value="">All Tags</option>
                  {tags.map((tag) => (
                    <option key={tag} value={tag}>
                      {tag}
                    </option>
                  ))}
                </select>
              )}

              {isLoggedIn && (
                <>
                  <FilterToggleButton
                    active={filterInterested}
                    onClick={() => setFilterInterested(!filterInterested)}
                    ariaLabel="Filter session cards you showed interest in"
                  >
                    My Interests
                  </FilterToggleButton>
                  {deviceId && (
                    <FilterToggleButton
                      active={filterMine}
                      onClick={() => setFilterMine(!filterMine)}
                      ariaLabel="Filter your own session cards"
                    >
                      My Cards
                    </FilterToggleButton>
                  )}
                </>
              )}
            </div>
          </div>
        </SessionBoardFilters>

        {/* Results count */}
        <p className="text-sm text-gray-500 mb-4">
          Showing {sortedProposals.length} of {proposals.length} session cards
        </p>

        {/* Proposals list */}
        {sortedProposals.length === 0 ? (
          <NoResultsMessage
            hasItems={proposals.length > 0}
            emptyMessage="No session cards yet. Be the first to submit one!"
            filteredMessage="No session cards match your filters."
            onClearFilters={proposals.length > 0 && hasActiveFilters ? clearFilters : undefined}
          />
        ) : (
          <div className="space-y-4">
            {sortedProposals.map((proposal) => {
              const isOwner = deviceId && proposal.createdByDeviceId === deviceId;
              const canEdit = canPropose && (isOrganizer || isOwner);
              return (
                <ProposalCard
                  key={proposal.id}
                  proposal={proposal}
                  onClick={() => setSelectedProposal(proposal)}
                  hiddenFields={hiddenFields}
                  defaultSlotMinutes={defaultSlotMinutes}
                  sessionInfo={proposalSessionMap.get(proposal.id) ?? null}
                  voted={votedProposalIds.has(proposal.id)}
                  onEdit={canEdit ? () => setEditingProposal(proposal) : undefined}
                  onDelete={
                    canEdit && !proposal.deletedAt
                      ? async () => {
                          if (!eventId) return;
                          try { await deleteProposal(eventId, proposal.id); refresh(); } catch { /* ignore */ }
                        }
                      : undefined
                  }
                  onRestore={
                    isOwner && proposal.deletedAt
                      ? async () => {
                          if (!eventId) return;
                          try { await restoreProposal(eventId, proposal.id); refresh(); } catch { /* ignore */ }
                        }
                      : undefined
                  }
                />
              );
            })}
          </div>
        )}

        {/* Create Proposal Modal */}
        <CreateProposalModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          template={template}
          slotSpanOptions={slotSpanOptions}
          defaultSlotMinutes={defaultSlotMinutes}
          onCreate={handleCreateProposal}
          autoFillFields={autoFillFields}
          participants={participants}
        />

        {/* View Proposal Modal */}
        {selectedProposal && (
          <ViewProposalModal
            proposal={selectedProposal}
            onClose={() => setSelectedProposal(null)}
            hiddenFields={hiddenFields}
            defaultSlotMinutes={defaultSlotMinutes}
            voted={votedProposalIds.has(selectedProposal.id)}
          />
        )}

        {editingProposal && (
          <EditProposalModal
            proposal={editingProposal}
            template={template}
            slotSpanOptions={slotSpanOptions}
            defaultSlotMinutes={defaultSlotMinutes}
            onClose={() => setEditingProposal(null)}
            autoFillFields={autoFillFields}
            participants={participants}
            onSave={async (data) => {
              await updateProposal(eventId!, editingProposal.id, data);
              refresh();
              setEditingProposal(null);
            }}
          />
        )}
      </div>
    </Layout>
  );
}

function CreateProposalModal({
  isOpen,
  onClose,
  template,
  slotSpanOptions,
  defaultSlotMinutes,
  onCreate,
  autoFillFields,
  participants,
}: {
  isOpen: boolean;
  onClose: () => void;
  template: Template | null;
  slotSpanOptions: number[];
  defaultSlotMinutes: number;
  onCreate: (data: ProposalFormData) => Promise<void>;
  autoFillFields: Record<string, string>;
  participants: Participant[];
}) {
  const [formData, setFormData] = useState<ProposalFormData>({
    title: '',
    description: '',
    fields: {},
    nickname: '',
    people: [{ nickname: '', role: 'presenter' }],
    preferredSlotSpan: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load saved nickname and set first person
  React.useEffect(() => {
    getNickname().then((nick) => {
      if (nick) {
        setFormData((prev) => ({
          ...prev,
          nickname: nick,
          people: prev.people?.length ? [{ ...prev.people[0], nickname: nick }, ...prev.people.slice(1)] : [{ nickname: nick, role: 'presenter' }],
        }));
      }
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    const people = (formData.people ?? []).filter((p) => p.nickname.trim());
    if (!people.length) {
      setError('At least one name is required');
      setIsSubmitting(false);
      return;
    }
    const submitData: ProposalFormData = {
      ...formData,
      nickname: people[0].nickname.trim(),
      people: people.map((p) => ({ nickname: p.nickname.trim(), role: p.role || 'presenter' })),
      fields: { ...autoFillFields, ...formData.fields },
    };
    try {
      await onCreate(submitData);
      setFormData({ title: '', description: '', fields: {}, nickname: submitData.nickname, people: [{ nickname: submitData.nickname, role: 'presenter' }], preferredSlotSpan: null });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create session card');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Submit a Session Card"
      footer={
        <>
          <button onClick={onClose} className="btn btn-secondary" disabled={isSubmitting}>
            Cancel
          </button>
          <button
            type="submit"
            form="create-proposal-form"
            className="btn btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Session Card'}
          </button>
        </>
      }
    >
      <form id="create-proposal-form" onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div>
          <label className="label">
            Presenter(s) *
          </label>
          <p className="text-sm text-gray-500 mb-2">
            Your name is pre-filled. You can change it to create a card for someone else, or add co-presenters.
            The card stays in your vault for editing regardless.
          </p>
          {(formData.people ?? [{ nickname: formData.nickname, role: 'presenter' }]).map((person, idx) => (
            <div key={idx} className="flex gap-2 mb-2">
              <div className="flex-1">
                <PersonPicker
                  value={person.nickname}
                  onChange={(v) => {
                    const next = [...(formData.people ?? [])];
                    if (!next[idx]) next[idx] = { nickname: '', role: 'presenter' };
                    next[idx] = { ...next[idx], nickname: v };
                    setFormData({ ...formData, people: next, nickname: next[0]?.nickname ?? formData.nickname });
                  }}
                  participants={participants}
                  placeholder={idx === 0 ? 'Presenter name (defaults to you)' : 'Co-presenter name'}
                />
              </div>
              {(formData.people ?? []).length > 1 && (
                <button
                  type="button"
                  onClick={() => {
                    const next = (formData.people ?? []).filter((_, i) => i !== idx);
                    setFormData({ ...formData, people: next, nickname: next[0]?.nickname ?? '' });
                  }}
                  className="btn btn-secondary"
                  aria-label="Remove person"
                >
                  Remove
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={() => setFormData({ ...formData, people: [...(formData.people ?? []), { nickname: '', role: 'copresenter' }] })}
            className="btn btn-secondary text-sm"
          >
            + Add another person
          </button>
        </div>

        <div>
          <label htmlFor="title" className="label">
            Session Title *
          </label>
          <input
            id="title"
            type="text"
            className="input"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            required
            placeholder="What's your session about?"
          />
        </div>

        <div>
          <label htmlFor="description" className="label">
            Description
          </label>
          <textarea
            id="description"
            className="input min-h-[100px]"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Tell us more about your session..."
          />
        </div>

        {slotSpanOptions.length > 0 && (
          <div>
            <label htmlFor="preferredSlotSpan" className="label">
              Preferred session length
            </label>
            <select
              id="preferredSlotSpan"
              className="select"
              value={formData.preferredSlotSpan ?? ''}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  preferredSlotSpan: e.target.value === '' ? null : Number(e.target.value),
                })
              }
            >
              <option value="">No preference</option>
              {slotSpanOptions.map((n) => (
                <option key={n} value={n}>
                  {n * defaultSlotMinutes} min{n !== 1 ? ` (${n} slots)` : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Language, Format, Audience Level, Tags – order matches Admin settings */}
        <TemplateFields
          template={template}
          fields={formData.fields}
          onChange={(name, value) =>
            setFormData({ ...formData, fields: { ...formData.fields, [name]: value } })
          }
          idPrefix="create-"
        />
      </form>
    </Modal>
  );
}

function EditProposalModal({
  proposal,
  template,
  slotSpanOptions,
  defaultSlotMinutes,
  onClose,
  onSave,
  autoFillFields,
  participants,
}: {
  proposal: Proposal;
  template: Template | null;
  slotSpanOptions: number[];
  defaultSlotMinutes: number;
  onClose: () => void;
  onSave: (data: ProposalFormData) => Promise<void>;
  autoFillFields: Record<string, string>;
  participants: Participant[];
}) {
  const initialPeople = proposal.people?.length
    ? proposal.people
    : [{ nickname: proposal.createdByNickname, role: 'presenter' as const }];
  const [formData, setFormData] = useState<ProposalFormData>({
    title: proposal.title,
    description: proposal.description ?? '',
    fields: { ...proposal.fields },
    nickname: proposal.createdByNickname,
    people: initialPeople,
    preferredSlotSpan: proposal.preferredSlotSpan ?? null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    const people = proposal.people?.length ? proposal.people : [{ nickname: proposal.createdByNickname, role: 'presenter' as const }];
    setFormData({
      title: proposal.title,
      description: proposal.description ?? '',
      fields: { ...proposal.fields },
      nickname: proposal.createdByNickname,
      people,
      preferredSlotSpan: proposal.preferredSlotSpan ?? null,
    });
  }, [proposal]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const people = (formData.people ?? []).filter((p) => p.nickname.trim());
    if (!people.length) {
      setError('At least one name is required');
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      await onSave({
        ...formData,
        nickname: people[0].nickname.trim(),
        people: people.map((p) => ({ nickname: p.nickname.trim(), role: p.role || 'presenter' })),
        fields: { ...autoFillFields, ...formData.fields },
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update session card');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Edit Session Card"
      footer={
        <>
          <button onClick={onClose} className="btn btn-secondary" disabled={isSubmitting}>
            Cancel
          </button>
          <button type="submit" form="edit-proposal-form" className="btn btn-primary" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save'}
          </button>
        </>
      }
    >
      <form id="edit-proposal-form" onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">{error}</div>
        )}
        <div>
          <label className="label">Presenter(s) *</label>
          <p className="text-sm text-gray-500 mb-2">
            You can pick known participants or type a new name.
          </p>
          {(formData.people ?? []).map((person, idx) => (
            <div key={idx} className="flex gap-2 mb-2">
              <div className="flex-1">
                <PersonPicker
                  value={person.nickname}
                  onChange={(v) => {
                    const next = [...(formData.people ?? [])];
                    if (!next[idx]) next[idx] = { nickname: '', role: 'presenter' };
                    next[idx] = { ...next[idx], nickname: v };
                    setFormData({ ...formData, people: next, nickname: next[0]?.nickname ?? '' });
                  }}
                  participants={participants}
                  placeholder={idx === 0 ? 'Main contact' : 'Co-presenter'}
                />
              </div>
              {(formData.people ?? []).length > 1 && (
                <button
                  type="button"
                  onClick={() => {
                    const next = (formData.people ?? []).filter((_, i) => i !== idx);
                    setFormData({ ...formData, people: next, nickname: next[0]?.nickname ?? '' });
                  }}
                  className="btn btn-secondary"
                  aria-label="Remove person"
                >
                  Remove
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={() => setFormData({ ...formData, people: [...(formData.people ?? []), { nickname: '', role: 'copresenter' }] })}
            className="btn btn-secondary text-sm"
          >
            + Add another person
          </button>
        </div>
        <div>
          <label htmlFor="edit-title" className="label">Session Title *</label>
          <input
            id="edit-title"
            type="text"
            className="input"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            required
          />
        </div>
        <div>
          <label htmlFor="edit-description" className="label">Description</label>
          <textarea
            id="edit-description"
            className="input min-h-[100px]"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
        </div>
        {slotSpanOptions.length > 0 && (
          <div>
            <label htmlFor="edit-preferredSlotSpan" className="label">Preferred session length</label>
            <select
              id="edit-preferredSlotSpan"
              className="select"
              value={formData.preferredSlotSpan ?? ''}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  preferredSlotSpan: e.target.value === '' ? null : Number(e.target.value),
                })
              }
            >
              <option value="">No preference</option>
              {slotSpanOptions.map((n) => (
                <option key={n} value={n}>{n * defaultSlotMinutes} min{n !== 1 ? ` (${n} slots)` : ''}</option>
              ))}
            </select>
          </div>
        )}
        {/* Language, Format, Audience Level, Tags – order matches Admin settings */}
        <TemplateFields
          template={template}
          fields={formData.fields}
          onChange={(name, value) =>
            setFormData({ ...formData, fields: { ...formData.fields, [name]: value } })
          }
          idPrefix="edit-"
        />
      </form>
    </Modal>
  );
}

function ViewProposalModal({
  proposal,
  onClose,
  hiddenFields,
  defaultSlotMinutes,
  voted,
}: {
  proposal: Proposal;
  onClose: () => void;
  hiddenFields?: Set<string>;
  defaultSlotMinutes?: number;
  voted?: boolean;
}) {
  const peopleLabel = proposal.people?.length
    ? proposal.people.map((p) => p.nickname).join(', ')
    : proposal.createdByNickname;

  const durationLabel = proposal.preferredSlotSpan && defaultSlotMinutes
    ? `${proposal.preferredSlotSpan * defaultSlotMinutes} min`
    : null;

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={proposal.title}
    >
      <div className="space-y-4">
        <div>
          <span className="text-sm text-gray-500">Presenter(s)</span>
          <p className="font-medium">{peopleLabel}</p>
        </div>

        {durationLabel && (
          <div>
            <span className="text-sm text-gray-500">Session length</span>
            <p className="font-medium">{durationLabel}</p>
          </div>
        )}

        {proposal.description && (
          <div>
            <span className="text-sm text-gray-500">Description</span>
            <p className="whitespace-pre-wrap">{proposal.description}</p>
          </div>
        )}

        {proposal.fields && Object.entries(proposal.fields).some(([k, v]) => v && !hiddenFields?.has(k)) && (
          <div className="grid gap-2">
            {Object.entries(proposal.fields).map(([key, value]) => (
              value && !hiddenFields?.has(key) && (
                <div key={key}>
                  <span className="text-sm text-gray-500 capitalize">{key}</span>
                  <p>{value}</p>
                </div>
              )
            ))}
          </div>
        )}

        {voted && (
          <div className="pt-4 border-t border-gray-200">
            <span className="voted-badge" title="You showed interest in this session card">
              <svg className="w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              You showed interest in this session card
            </span>
          </div>
        )}
      </div>
    </Modal>
  );
}
